import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Phone, Mail, Trash2, Inbox } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = [
  ["nuova", "Nuova", "border-gold/50 bg-gold/10 text-yellow-700"],
  ["contattato", "Contattato", "border-azure/40 bg-azure/10 text-brandblue"],
  ["in_lavorazione", "In lavorazione", "border-navy/25 bg-navy/5 text-navy"],
  ["chiusa", "Chiusa", "border-green-300 bg-green-50 text-green-700"],
];

export default function LeadsAdmin() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem("sf-admin-token") || "";
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/leads`, auth);
      setLeads(data);
    } catch {
      toast.error("Errore nel caricamento delle richieste");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (lead, status) => {
    try {
      await axios.patch(`${API}/admin/leads/${lead.id}`, { status }, auth);
      setLeads((ls) => ls.map((l) => (l.id === lead.id ? { ...l, status } : l)));
      toast.success("Stato aggiornato");
    } catch {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const remove = async (lead) => {
    if (!window.confirm(`Eliminare la richiesta di ${lead.nome}?`)) return;
    try {
      await axios.delete(`${API}/admin/leads/${lead.id}`, auth);
      setLeads((ls) => ls.filter((l) => l.id !== lead.id));
      toast.success("Richiesta eliminata");
    } catch {
      toast.error("Errore nell'eliminazione");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 size={28} className="animate-spin text-azure" />
      </div>
    );
  }

  return (
    <div data-testid="admin-leads-section">
      <div className="mb-6">
        <h1 className="font-serif font-bold text-3xl">Richieste di valutazione</h1>
        <p className="text-sm text-ink/50 mt-1">{leads.length} richieste ricevute dal modulo contatti</p>
      </div>

      {leads.length === 0 ? (
        <div className="bg-white rounded-2xl border border-ink/10 p-12 text-center" data-testid="admin-leads-empty">
          <Inbox size={32} className="mx-auto text-ink/25 mb-3" />
          <p className="text-ink/50 text-sm">Nessuna richiesta ancora. Le nuove richieste arrivano anche via email a info@semplificaffitto.it e info@edilstudiofi.it.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="admin-leads-list">
          {leads.map((l) => (
            <div key={l.id} data-testid={`admin-lead-${l.id}`} className="bg-white rounded-2xl border border-ink/10 px-5 py-4">
              <div className="flex flex-wrap items-start gap-4 justify-between">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <p className="font-semibold text-sm">{l.nome}</p>
                    <span className="text-xs text-ink/40">
                      {new Date(l.created_at).toLocaleDateString("it-IT", { day: "numeric", month: "short", year: "numeric" })}
                    </span>
                    {l.fonte && l.fonte !== "sito" && (
                      <span className="text-[11px] font-semibold rounded-full px-2.5 py-0.5 bg-gold/15 text-amber-700" data-testid={`lead-fonte-${l.id}`}>
                        {l.fonte}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-4 mt-1.5 text-xs text-ink/60 flex-wrap">
                    <a href={`tel:${l.telefono}`} className="inline-flex items-center gap-1.5 hover:text-brandblue transition-colors" data-testid={`lead-phone-${l.id}`}>
                      <Phone size={12} /> {l.telefono}
                    </a>
                    <a href={`mailto:${l.email}`} className="inline-flex items-center gap-1.5 hover:text-brandblue transition-colors" data-testid={`lead-email-${l.id}`}>
                      <Mail size={12} /> {l.email}
                    </a>
                  </div>
                  <p className="text-xs text-ink/50 mt-1.5">
                    {l.citta} · {l.tipologia}
                    {l.note ? ` — ${l.note}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <select
                    value={l.status || "nuova"}
                    onChange={(e) => setStatus(l, e.target.value)}
                    data-testid={`lead-status-${l.id}`}
                    className={`text-xs font-semibold rounded-full border px-3 py-1.5 cursor-pointer focus:outline-none ${STATUSES.find(([v]) => v === (l.status || "nuova"))?.[2] || ""}`}
                  >
                    {STATUSES.map(([v, label]) => (
                      <option key={v} value={v}>{label}</option>
                    ))}
                  </select>
                  <button onClick={() => remove(l)} className="p-2 text-ink/30 hover:text-red-500 transition-colors" aria-label="Elimina richiesta" data-testid={`lead-delete-${l.id}`}>
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
